import { Form, useNavigate, useRevalidator } from "react-router";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";

import FileUploader from "./FileUploader.jsx";
import TagSelectDropdown from "./TagSelectDropdown.jsx";
import StandardRefsDropdown from "./StandardRefsDropdown.jsx";
import DeleteConfirmModal from "./admin/DeleteConfirmModal.jsx";
import { useFileStore } from "../store/fileStore.js";
import { parseSemanticVersion } from "../utilities/versionHelper.js";
import api from "../utilities/api.js";
import {
  RECORD_FIELDS,
  valuesDiffer,
  normalizeStandardRefs,
} from "../utilities/recordDiff.js";
import { useAuthStore } from "../store/authStore.js";
import { recordPropType } from "../utilities/recordPropTypes";

export default function FieldEdit({
  method,
  field,
  originalField = null,
  isDraft = false,
  readOnly = false,
}) {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const [ofid, setOfid] = useState(field ? field.ofid : "");

  // State for available tags fetched from the API
  const [availableTags, setAvailableTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(true);

  // Create a state for tags, defaulting to the field's tag IDs if any.
  // field.tags may be populated objects or IDs, so extract IDs
  const [selectedTags, setSelectedTags] = useState(() => {
    if (!field?.tags || !Array.isArray(field.tags)) return [];
    return field.tags.map((tag) => (typeof tag === "object" ? tag._id : tag));
  });

  // Fetch tags from API on mount
  useEffect(() => {
    const fetchTags = async () => {
      try {
        setTagsLoading(true);
        const response = await api.get(`/tags`);
        setAvailableTags(response.data);
      } catch (error) {
        console.error("Error fetching tags:", error);
        toast.error("Failed to load tags");
      } finally {
        setTagsLoading(false);
      }
    };
    fetchTags();
  }, []);

  // Standard references (standard populated or ID) and the selectable standards
  const [standardRefs, setStandardRefs] = useState(field?.standardRefs || []);
  const [availableStandards, setAvailableStandards] = useState([]);

  // Fetch standards from API on mount (not needed for read-only display)
  useEffect(() => {
    if (readOnly) return;
    api
      .get(`/standards`)
      .then((response) => setAvailableStandards(response.data))
      .catch((error) => {
        console.error("Error fetching standards:", error);
        toast.error("Failed to load standards");
      });
  }, [readOnly]);

  // Helper to check if a tag name is in selectedTags (handles both ID and name lookups)
  const hasTagByName = (tagName) => {
    return availableTags.some(
      (tag) => selectedTags.includes(tag._id) && tag.name === tagName,
    );
  };

  // Create a state for the semantic versioning on the introduced field.
  const [introducedInput, setIntroducedInput] = useState(
    Array.isArray(field?.introduced)
      ? field.introduced.join(".")
      : "",
  );

  // Create a state for the semantic version on the deprecated field.
  const [deprecatedInput, setDeprecatedInput] = useState(
    Array.isArray(field?.deprecated)
      ? field.deprecated.join(".")
      : "",
  );

  // Create a state for wrong version format
  const [wrongIntroducedInput, setWrongIntroducedInput] = useState(false); // State for wrong input in Introduced field
  const [wrongDeprecatedInput, setWrongDeprecatedInput] = useState(false); // State for wrong input in Deprecated field

  // Access both the uploaded file and its setter from Zustand
  const uploadedFile = useFileStore((state) => state.uploadedFile);
  const setUploadedFile = useFileStore((state) => state.setUploadedFile);

  // Only clear the uploaded file if the field doesn't have one.
  useEffect(() => {
    if (field?.uploadedFile) {
      setUploadedFile(null);
    }
  }, [setUploadedFile, field]);

  function handleCancel() {
    navigate("..");
  }

  // Current values of uncontrolled inputs, keyed by input name, so the
  // visual diff updates while typing (not only after saving)
  const [liveValues, setLiveValues] = useState({});

  // Change events bubble up to the Form, so one handler covers all inputs
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name) setLiveValues((prev) => ({ ...prev, [name]: value }));
  };

  // Current (possibly unsaved) value of a field for the visual diff
  const getCurrentValue = (fieldName) => {
    switch (fieldName) {
      case "ofid":
        return ofid;
      case "tags":
        return selectedTags;
      case "standardRefs":
        return standardRefs;
      case "introduced":
        return parseSemanticVersion(introducedInput) || [];
      case "deprecated":
        return parseSemanticVersion(deprecatedInput) || [];
      case "uploadedFile":
        if (uploadedFile?.filename) return uploadedFile.filename;
        break;
    }
    return fieldName in liveValues ? liveValues[fieldName] : field[fieldName];
  };

  // Compare the current value of a field against a reference record
  const differsFrom = (fieldName, reference) =>
    valuesDiffer(fieldName, getCurrentValue(fieldName), reference?.[fieldName]);

  // Visual diff: compare current field values to original
  const isFieldChanged = (fieldName) => {
    if (!originalField || !isDraft) return false;
    return differsFrom(fieldName, originalField);
  };

  // Unsaved changes: compare current field values to the saved draft
  const hasUnsavedChanges = () =>
    !readOnly && RECORD_FIELDS.some((name) => differsFrom(name, field));

  // Leave the page, but warn first if there are unsaved changes
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const leavePage = (to, options) => {
    if (hasUnsavedChanges()) {
      setPendingNavigation({ to, options });
    } else {
      navigate(to, options);
    }
  };

  const labelClass = (fieldName) => {
    const base = "block uppercase tracking-wide text-sm font-bold mb-2";
    return isFieldChanged(fieldName)
      ? `${base} text-red-600`
      : `${base} text-gray-700`;
  };

  // Compute added/removed tag IDs for visual diff
  const originalTagIds = (originalField?.tags || []).map((tag) =>
    (typeof tag === "object" ? tag._id : tag).toString(),
  );
  const normalizedSelectedTags = selectedTags.map((id) => id.toString());
  const addedTagIds =
    isDraft && originalField
      ? normalizedSelectedTags.filter((id) => !originalTagIds.includes(id))
      : [];
  const removedTagIds =
    isDraft && originalField
      ? originalTagIds.filter((id) => !normalizedSelectedTags.includes(id))
      : [];

  const inputTextClass = (fieldName) =>
    isFieldChanged(fieldName) ? "text-red-600" : "text-gray-700";

  // Delete draft handler
  const handleDeleteDraft = async () => {
    try {
      await api.delete(`/records-drafts/${field._id}`);
      toast.success("Draft deleted");
      revalidator.revalidate();
      navigate("..");
    } catch {
      toast.error("Failed to delete draft");
    }
  };

  // Compute bgColor based on selectedTags so it updates immediately.
  // Use hasTagByName to check for "Under Review" tag by name
  const bgColor = hasTagByName("Under Review")
    ? "bg-yellow-100"
    : "bg-gray-200";

  // Handler for Form submission.
  function handleFormSubmit(e) {
    if (readOnly) {
      e.preventDefault();
      return;
    }

    setWrongIntroducedInput(false);
    setWrongDeprecatedInput(false);

    const parsedIntroduced = parseSemanticVersion(introducedInput);
    if (!parsedIntroduced) {
      e.preventDefault();
      setWrongIntroducedInput(true);
      toast.error(
        "Please enter a valid semantic version\n for Introduced (e.g., 2.0.15)",
        {
          duration: 5000,
          position: "top-center",
        },
      );
    }

    const parsedDeprecated = parseSemanticVersion(deprecatedInput);
    if (deprecatedInput && !parsedDeprecated) {
      e.preventDefault();
      setWrongDeprecatedInput(true);
      toast.error(
        "Please enter a valid semantic version\n for Deprecated (e.g., 2.1.0)",
        {
          duration: 5000,
          position: "top-center",
        },
      );
    }
  }

  return (
    <>
    <Form method={method} onSubmit={handleFormSubmit} onChange={handleFormChange}>
      <div className="flow-root mt-12 mb-12">
        {readOnly ? (
          <button
            type="button"
            onClick={() => navigate("..")}
            className="py-2 px-2 float-right text-center text-cyan-900 font-bold text-lg border-2 border-cyan-900 bg-white hover:bg-gray-300 hover:text-gray-800 rounded-lg transition duration-300"
          >
            Back
          </button>
        ) : (
          <>
            <button
              type="submit"
              className="py-2 px-4 float-right text-center text-gray-100 font-bold text-lg border-2 border-black bg-cyan-900 hover:bg-cyan-800 hover:text-white rounded-lg transition duration-300"
            >
              {method === "post" ? "Save New Field" : "Save Draft"}
            </button>
            {isDraft ? (
              <>
                <button
                  type="button"
                  onClick={handleDeleteDraft}
                  className="py-2 px-4 float-right mr-4 text-center text-gray-100 font-bold text-lg border-2 border-black bg-cyan-900 hover:bg-cyan-800 hover:text-white rounded-lg transition duration-300"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => leavePage("..")}
                  className="py-2 px-2 float-right mr-4 text-center text-cyan-900 font-bold text-lg border-2 border-cyan-900 bg-white hover:bg-gray-300 hover:text-gray-800 rounded-lg transition duration-300"
                >
                  Back
                </button>
                {isAdmin() && (
                  <button
                    type="button"
                    onClick={() => leavePage("/admin", { state: { tab: "changes" } })}
                    className="py-2 px-2 float-right mr-4 text-center text-cyan-900 font-bold text-lg border-2 border-cyan-900 bg-white hover:bg-gray-300 hover:text-gray-800 rounded-lg transition duration-300"
                  >
                    All Drafts
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={handleCancel}
                className="py-2 px-2 float-right mr-4 text-center text-cyan-900 font-bold text-lg border-2 border-cyan-900 bg-white hover:bg-gray-300 hover:text-gray-800 rounded-lg transition duration-300"
              >
                Cancel
              </button>
            )}
          </>
        )}
      </div>
      <div className="flex flex-wrap -mx-3">
        <div className="w-full md:w-1/3 px-3">
          <label className={labelClass("ofid")} htmlFor="ofid">
            OFID
          </label>
          <input
            className={`appearance-none block w-full min-h-12 ${bgColor} ${inputTextClass("ofid")} border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
            id="ofid"
            name="ofid"
            type="text"
            required
            readOnly={readOnly}
            value={ofid}
            onChange={(e) => setOfid(e.target.value)}
          />
        </div>

        <div className="w-full md:w-2/3 px-3">
          <label className={labelClass("fieldName")} htmlFor="fieldName">
            Field Name
          </label>
          <input
            className={`appearance-none block w-full min-h-12 ${bgColor} ${inputTextClass("fieldName")} border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
            id="fieldName"
            name="fieldName"
            type="text"
            required
            readOnly={readOnly}
            defaultValue={field ? field.fieldName : ""}
          />
        </div>
      </div>
      <div className="flex flex-wrap -mx-3">
        <div className="w-full md:w-1/6 px-3 flex flex-col">
          <label className={labelClass("dataType")} htmlFor="dataType">
            Data Type
          </label>
          <input
            className={`mt-auto appearance-none block w-full min-h-12 ${bgColor} ${inputTextClass("dataType")} border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
            id="dataType"
            name="dataType"
            type="text"
            readOnly={readOnly}
            defaultValue={field ? field.dataType : ""}
          />
        </div>

        <div className="w-full md:w-1/6 px-3 flex flex-col">
          <label className={labelClass("level")} htmlFor="level">
            Level
          </label>
          <input
            className={`mt-auto appearance-none block w-full min-h-12 ${bgColor} ${inputTextClass("level")} border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
            id="level"
            name="level"
            type="text"
            readOnly={readOnly}
            defaultValue={field ? field.level : ""}
          />
        </div>

        <div className="w-full md:w-1/6 px-3 flex flex-col">
          <label
            className={labelClass("linkReference")}
            htmlFor="linkReference"
          >
            Link Reference
          </label>
          <input
            className={`mt-auto appearance-none block w-full min-h-12 ${bgColor} ${inputTextClass("linkReference")} border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
            id="linkReference"
            name="linkReference"
            type="text"
            readOnly={readOnly}
            defaultValue={field ? field.linkReference : ""}
          />
        </div>

        <div className="w-full md:w-1/6 px-3 flex flex-col">
          <div className="flex flex-wrap items-center gap-x-4">
            <label className={labelClass("introduced")} htmlFor="introduced">
              Introduced in Version
            </label>
            <div
              className={`-mt-2 ${
                wrongIntroducedInput ? "text-red-600" : "text-gray-100"
              }`}
            >
              {" "}
              {wrongIntroducedInput
                ? "Use semantic versioning, e.g. 2.0.15"
                : ""}
            </div>
          </div>
          <input
            className={`mt-auto appearance-none block w-full min-h-12 ${bgColor} ${inputTextClass("introduced")} border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
            id="introduced"
            name="introduced"
            type="text"
            readOnly={readOnly}
            value={introducedInput}
            onChange={(e) => setIntroducedInput(e.target.value)}
          />
          <input
            type="hidden"
            id="introducedArray"
            name="introducedArray"
            value={JSON.stringify(parseSemanticVersion(introducedInput) || [])}
          />
        </div>

        <div className="w-full md:w-1/6 px-3 flex flex-col">
          <div className="flex flex-wrap items-center gap-x-4">
            <label className={labelClass("deprecated")} htmlFor="deprecated">
              Valid until Version
            </label>
            <div
              className={`-mt-2 ${
                wrongDeprecatedInput ? "text-red-600" : "text-gray-100"
              }`}
            >
              {" "}
              {wrongDeprecatedInput
                ? "Use semantic versioning, e.g. 2.1.0"
                : ""}
            </div>
          </div>
          <input
            className={`mt-auto appearance-none block w-full min-h-12 ${bgColor} ${inputTextClass("deprecated")} border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
            id="deprecated"
            name="deprecated"
            type="text"
            readOnly={readOnly}
            value={deprecatedInput}
            onChange={(e) => setDeprecatedInput(e.target.value)}
          />
          <input
            type="hidden"
            id="deprecatedArray"
            name="deprecatedArray"
            value={JSON.stringify(parseSemanticVersion(deprecatedInput) || [])}
          />
        </div>

        <div className="w-full md:w-1/6 px-3 flex flex-col">
          <label className={labelClass("tags")} htmlFor="tags">
            Tags
          </label>
          <div className="mt-auto mb-3">
            {tagsLoading ? (
              <div
                className={`flex items-center min-h-12 px-4 py-3 ${bgColor} text-gray-500 border border-gray-700 rounded`}
              >
                Loading tags...
              </div>
            ) : (
              <TagSelectDropdown
                availableTags={availableTags}
                selectedTags={selectedTags}
                onSelectionChange={setSelectedTags}
                labelPrefix="Tags"
                bgColor={bgColor}
                addedTagIds={addedTagIds}
                removedTagIds={removedTagIds}
                readOnly={readOnly}
              />
            )}
          </div>
          {/* Hidden inputs for form submission - submit tag IDs */}
          {selectedTags.map((tagId) => (
            <input key={tagId} type="hidden" name="tags" value={tagId} />
          ))}
        </div>
      </div>
      {/* Read-only: show nothing if neither the draft nor the original has references */}
      {(!readOnly ||
        standardRefs.length > 0 ||
        (isDraft && originalField?.standardRefs?.length > 0)) && (
        <div className="flex flex-wrap -mx-3">
          <div className="w-full px-3">
            <div className={labelClass("standardRefs")}>Standard References</div>
            <div className="mb-3">
              <StandardRefsDropdown
                standardRefs={standardRefs}
                standards={availableStandards}
                onChange={setStandardRefs}
                originalRefs={
                  isDraft && originalField
                    ? originalField.standardRefs || []
                    : null
                }
                bgColor={bgColor}
                readOnly={readOnly}
              />
            </div>
            {/* Hidden input for form submission - normalised references as JSON */}
            <input
              type="hidden"
              name="standardRefs"
              value={JSON.stringify(normalizeStandardRefs(standardRefs))}
            />
          </div>
        </div>
      )}
      <div className="flex flex-wrap -mx-3">
        <div className="w-full px-3">
          <label className={labelClass("values")} htmlFor="values">
            Values
          </label>
          <textarea
            className={`appearance-none block w-full min-h-12 ${bgColor} ${inputTextClass("values")} border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
            id="values"
            name="values"
            readOnly={readOnly}
            defaultValue={field ? field.values : ""}
          />
        </div>
      </div>
      <div className="flex flex-wrap -mx-3">
        <div className="w-full px-3">
          <label className={labelClass("example")} htmlFor="example">
            Example
          </label>
          <textarea
            className={`appearance-none block w-full min-h-12 ${bgColor} ${inputTextClass("example")} border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
            id="example"
            name="example"
            readOnly={readOnly}
            defaultValue={field ? field.example : ""}
          />
        </div>
      </div>
      <div className="flex flex-wrap -mx-3">
        <div className="w-full px-3">
          <label className={labelClass("description")} htmlFor="description">
            Description
          </label>
          <textarea
            className={`appearance-none block w-full min-h-12 ${bgColor} ${inputTextClass("description")} border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
            id="description"
            name="description"
            readOnly={readOnly}
            defaultValue={field ? field.description : ""}
          />
        </div>
      </div>
      {!readOnly && (
        <div className="flex flex-wrap -mx-3">
          <div className="w-full px-3">
            {/* If there's a stored file and no new uploaded file from the FileUploader, show a visible text input.
          The user can clear this field to remove the file. */}
            {field?.uploadedFile && !uploadedFile && (
              <div className="mt-2">
                <label
                  htmlFor="storedFile"
                  className={labelClass("uploadedFile")}
                >
                  Currently Stored File (delete to remove or upload a new one to
                  replace):
                </label>
                <input
                  type="text"
                  id="storedFile"
                  name="uploadedFile"
                  defaultValue={field.uploadedFile}
                  className={`appearance-none block w-full min-h-12 ${bgColor} ${inputTextClass("uploadedFile")} border border-gray-700 rounded py-3 px-4 mb-3 leading-tight`}
                  placeholder="Clear to delete file"
                />
              </div>
            )}
            <label
              className="block uppercase tracking-wide text-gray-700 text-sm font-bold mb-2"
              htmlFor="fileUpload"
            >
              File Upload
            </label>

            <div className="w-full">
              <FileUploader ofid={ofid} bgColor={bgColor} />
            </div>

            {/* Hidden input to capture uploaded file metadata */}
            {uploadedFile?.filename && (
              <input
                type="hidden"
                id="fileUpload"
                name="uploadedFile"
                value={uploadedFile.filename}
              />
            )}
          </div>
        </div>
      )}
    </Form>
    <DeleteConfirmModal
      isOpen={!!pendingNavigation}
      onClose={() => setPendingNavigation(null)}
      onConfirm={() => {
        const { to, options } = pendingNavigation;
        setPendingNavigation(null);
        navigate(to, options);
      }}
      title="Unsaved Changes"
      message="You have unsaved changes to this draft. If you leave now, they will be lost."
      confirmLabel="Leave without saving"
    />
    </>
  );
}

FieldEdit.propTypes = {
  method: PropTypes.string.isRequired,
  field: recordPropType,
  originalField: recordPropType,
  isDraft: PropTypes.bool,
  readOnly: PropTypes.bool,
};
